/**
 * Cohort React Query Hooks
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@services/queryClient";
import * as cohortService from "@services/cohorts";
import type {
  CohortCreateRequest,
  CohortUpdateRequest,
  CohortMemberBulkCreateRequest,
  CohortStatus,
  CohortMemberStatus,
} from "@/types/cohorts";

// Query params
export interface CohortListParams {
  skip?: number;
  limit?: number;
  status?: CohortStatus;
  workshop_id?: string;
}

export interface CohortMemberListParams {
  skip?: number;
  limit?: number;
  status?: CohortMemberStatus;
}

/**
 * Fetch all cohorts with optional filters
 */
export function useCohorts(params?: CohortListParams) {
  return useQuery({
    queryKey: queryKeys.cohort.list(params as Record<string, unknown>),
    queryFn: () => cohortService.getCohorts(params),
  });
}

/**
 * Fetch a single cohort by ID
 */
export function useCohort(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cohort.detail(id ?? ""),
    queryFn: () => cohortService.getCohort(id!),
    enabled: !!id,
  });
}

/**
 * Fetch cohorts by workshop ID
 */
export function useCohortsByWorkshop(workshopId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cohort.byWorkshop(workshopId ?? ""),
    queryFn: () =>
      cohortService.getCohorts({ workshop_id: workshopId }),
    enabled: !!workshopId,
  });
}

/**
 * Create a new cohort
 */
export function useCreateCohort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CohortCreateRequest) => cohortService.createCohort(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohort.all });
    },
  });
}

/**
 * Update a cohort
 */
export function useUpdateCohort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CohortUpdateRequest }) =>
      cohortService.updateCohort(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.cohort.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.cohort.all });
    },
  });
}

/**
 * Delete a cohort
 */
export function useDeleteCohort() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cohortService.deleteCohort(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cohort.all });
    },
  });
}

/**
 * Fetch members of a cohort
 */
export function useCohortMembers(
  cohortId: string | undefined,
  params?: CohortMemberListParams
) {
  return useQuery({
    queryKey: queryKeys.cohort.members(cohortId ?? ""),
    queryFn: () => cohortService.getCohortMembers(cohortId!, params),
    enabled: !!cohortId,
  });
}

/**
 * Add members to a cohort (bulk)
 */
export function useAddCohortMembers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cohortId,
      data,
    }: {
      cohortId: string;
      data: CohortMemberBulkCreateRequest;
    }) => cohortService.addCohortMembers(cohortId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.cohort.members(variables.cohortId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.cohort.detail(variables.cohortId),
      });
    },
  });
}

/**
 * Remove a member from a cohort
 */
export function useRemoveCohortMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cohortId,
      memberId,
    }: {
      cohortId: string;
      memberId: string;
    }) => cohortService.removeCohortMember(cohortId, memberId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.cohort.members(variables.cohortId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.cohort.detail(variables.cohortId),
      });
    },
  });
}

/**
 * Sync cohort with Google Group
 */
export function useSyncGoogleGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cohortId: string) => cohortService.syncGoogleGroup(cohortId),
    onSuccess: (_, cohortId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.cohort.members(cohortId),
      });
    },
  });
}

/**
 * Get cohort info by invite code (public, no auth required)
 */
export function useCohortInvite(inviteCode: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cohort.invite(inviteCode ?? ""),
    queryFn: () => cohortService.getCohortByInviteCode(inviteCode!),
    enabled: !!inviteCode,
    retry: false, // Don't retry on 404
  });
}

/**
 * Enroll in a cohort via invite code
 */
export function useEnrollInCohort() {
  return useMutation({
    mutationFn: ({ inviteCode, email }: { inviteCode: string; email: string }) =>
      cohortService.enrollInCohort(inviteCode, { email }),
  });
}
